import PrintIcon from '@mui/icons-material/Print';
import { Button, IconButton, Tooltip } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePrint } from './usePrint';

interface ExportButtonProps {
  /** ID(s) of the PrintableSection(s) to export */
  targetId: string | string[];
  /** Button label (only shown for variant="contained" or "outlined") */
  label?: string;
  /** Button variant */
  variant?: 'text' | 'outlined' | 'contained' | 'icon';
  /** Tooltip text */
  tooltip?: string;
  /** Custom filename for the PDF (user will see this as default name) */
  filename?: string;
  /** Callback fired before printing */
  onBeforePrint?: () => void;
  /** Callback fired after printing */
  onAfterPrint?: () => void;
  /** Additional CSS to apply during print */
  customPrintStyles?: string;
}

/**
 * A button component that triggers the browser's print dialog.
 * Users can then save as PDF or print to a physical printer.
 * 
 * @example
 * ```tsx
 * // Icon button
 * <ExportButton 
 *   targetId="compliance-table" 
 *   variant="icon"
 *   tooltip="Export to PDF"
 * />
 * 
 * // Full button
 * <ExportButton 
 *   targetId="compliance-table" 
 *   variant="contained"
 *   label="Export to PDF"
 * />
 * 
 * // Export multiple sections
 * <ExportButton 
 *   targetId={["graph-section", "table-section"]} 
 *   label="Export Report"
 * />
 * ```
 */
export const ExportButton: React.FC<ExportButtonProps> = ({
  targetId,
  label,
  variant = 'icon',
  tooltip,
  filename,
  onBeforePrint,
  onAfterPrint,
  customPrintStyles,
}) => {
  const { t } = useTranslation('common');
  const resolvedLabel = label ?? t('exportToPdf');
  const resolvedTooltip = tooltip ?? t('exportToPdf');
  const { handlePrint } = usePrint({
    targetId,
    filename,
    onBeforePrint,
    onAfterPrint,
    customPrintStyles,
  });

  if (variant === 'icon') {
    return (
      <Tooltip title={resolvedTooltip}>
        <IconButton
          onClick={handlePrint}
          color="primary"
          aria-label="export to pdf"
          size="medium"
        >
          <PrintIcon />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={resolvedTooltip}>
      <Button
        variant={variant === 'contained' ? 'contained' : variant === 'outlined' ? 'outlined' : 'text'}
        color="primary"
        startIcon={<PrintIcon />}
        onClick={handlePrint}
        aria-label="export to pdf"
      >
        {resolvedLabel}
      </Button>
    </Tooltip>
  );
};

export default ExportButton;

